import { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { Button, Input } from '../components/ui';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      await apiClient.post('/api/auth/forgot-password', { email });
    } catch (_) {
      // Always show success — prevent email enumeration
    } finally {
      setIsLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Forgot password</h2>
          <p className="text-sm text-slate-500 mb-6">
            Enter your email and we'll send a temporary password.
          </p>

          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
                If that email exists in our system, a temporary password has been sent. Check your inbox.
              </div>
              <p className="text-sm text-slate-600">
                Once you have it, go to{' '}
                <Link to="/reset-password" className="text-primary-600 hover:underline">
                  Reset Password
                </Link>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Email address"
                type="email"
                autoFocus
                placeholder="you@rhaya.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" isLoading={isLoading} className="w-full">
                Send temporary password
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 hover:underline">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [form, setForm] = useState({ tempPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post('/api/auth/reset-password', {
        tempPassword: form.tempPassword,
        newPassword: form.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error ?? 'Invalid or expired temporary password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Reset password</h2>
          <p className="text-sm text-slate-500 mb-6">
            Enter the temporary password from your email, then choose a new password.
          </p>

          {success ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
                Password reset successfully. You can now log in with your new password.
              </div>
              <Link to="/login">
                <Button className="w-full">Go to login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Temporary password (from email)"
                name="tempPassword"
                type="text"
                autoFocus
                placeholder="Paste temporary password"
                value={form.tempPassword}
                onChange={handleChange}
              />
              <Input
                label="New password"
                name="newPassword"
                type="password"
                placeholder="Min 8 characters"
                value={form.newPassword}
                onChange={handleChange}
              />
              <Input
                label="Confirm new password"
                name="confirmPassword"
                type="password"
                placeholder="Repeat new password"
                value={form.confirmPassword}
                onChange={handleChange}
              />

              {error && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <Button type="submit" isLoading={isLoading} className="w-full">
                Set new password
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 hover:underline">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
